import {Paper} from '@mantine/core';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalCounterpartFields from 'chemistry/molecules/ChemicalCounterpartFields';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import useCounterpartDetection from 'chemistry/molecules/useCounterpartDetection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {withLoadingScreen} from 'util/Promises';

export interface EditChemicalStructureModalProps {
  moleculeId: string;
  expectedCurrentVersionId: string;
  initialChemicalStructure: ChemicalStructureDrawerValue;
  schemaId?: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export function EditChemicalStructureModal({
  moleculeId,
  expectedCurrentVersionId,
  initialChemicalStructure,
  schemaId,
  onRequestClose,
  onRequestSubmit,
}: EditChemicalStructureModalProps): JSX.Element {
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [saving, setSaving] = useState(false);

  const counterpartDetection = useCounterpartDetection(
    sketcherValue,
    schemaId,
    BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE
  );

  const disabled =
    sketcherValue == null ||
    sketcherValue === initialChemicalStructure ||
    saving ||
    counterpartDetection.hasBlockingError ||
    counterpartDetection.isParsing;

  async function updateMoleculeChemicalStructure(): Promise<void> {
    setSaving(true);

    try {
      await withLoadingScreen(
        MoleculeActions.updateChemicalStructure(
          moleculeId,
          expectedCurrentVersionId,
          ChemicalFormatType.MOLFILE,
          sketcherValue!
        ),
        'Updating molecule...'
      );
      onRequestSubmit();
    } catch (err) {
      setSaving(false);
      throw err;
    }
  }

  return (
    <ExpandableModal
      onRequestClose={onRequestClose}
      onRequestSubmit={updateMoleculeChemicalStructure}
      startMaximized={true}
      title="Edit chemical structure"
      buttons={[{disabled, submit: true, text: 'Save'}]}
    >
      {BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE && (
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="Saving will update the chemical structure but not the Molecule Compound link. Links are validated at registration."
        />
      )}
      <Paper withBorder={BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE} radius="sm" p="md" mt="md">
        <ChemicalStructureModalInput
          initialSketcherValue={initialChemicalStructure}
          onSketcherValueChange={setSketcherValue}
          mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
          inputFormat={null}
          outputFormat={MarvinChemicalFormat.MOLV3}
        />
        {BenchlingConfig.CHEMISTRY_ENABLE_STOICHIOMETRY_TABLE && (
          <ChemicalCounterpartFields
            parseResponse={counterpartDetection.parseResponse}
            isLoading={counterpartDetection.isLoading}
            error={counterpartDetection.error}
            errorCode={counterpartDetection.errorCode}
          />
        )}
      </Paper>
    </ExpandableModal>
  );
}
